const NUMBER_SOURCE = String.raw`\$?\s*(?:\d[\d,]*(?:\.\d*)?|\.\d+)`;
const NUMBER_RE = new RegExp(`^${NUMBER_SOURCE}$`);
const SIGNED_NUMBER_RE = new RegExp(`^([-+])?\\s*(${NUMBER_SOURCE})$`);
const COMPARE_RE = new RegExp(`^(>=|<=|>|<|=)\\s*(${NUMBER_SOURCE})$`);
const RANGE_RE = new RegExp(`^(${NUMBER_SOURCE})\\s*(?:\\.\\.|–|—|-|to)\\s*(${NUMBER_SOURCE})$`, 'i');
const CARD_QUERY_RE = /^(?:(?:[·•*xX#]+)|(?:card:|last4:|last5:))\s*(\d{2,8})$/i;

const EPSILON = 0.005;

function toNumber(token) {
  if (typeof token !== 'string') return null;
  const cleaned = token.replace(/[$,\s]/g, '');
  if (!cleaned || !/^\d*\.?\d*$/.test(cleaned) || cleaned === '.') return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

function hasCents(token) {
  return /\.\d/.test(token);
}

// A whole-dollar figure means the dollar bucket, so `45` covers $45.00 through $45.99.
function upperBound(value, token) {
  return hasCents(token) ? value : value + 1 - EPSILON * 2;
}

/**
 * Interprets a search term as an amount query. Currency symbols, thousands
 * separators and surrounding whitespace are optional everywhere.
 *
 *   45        45.99      $45      $1,234.56
 *   >100      <=50       =45.00
 *   100-200   100..200   100 to 200
 *   -45 (money out)      +45 (money in)
 *
 * Returns null when the term is not an amount query.
 */
export function parseAmountQuery(rawTerm) {
  const term = String(rawTerm ?? '').trim();
  if (!term) return null;

  const range = term.match(RANGE_RE);
  if (range && NUMBER_RE.test(range[1]) && NUMBER_RE.test(range[2])) {
    const first = toNumber(range[1]);
    const second = toNumber(range[2]);
    if (first !== null && second !== null) {
      const highToken = first <= second ? range[2] : range[1];
      const min = Math.min(first, second);
      const max = upperBound(Math.max(first, second), highToken);
      return {
        type: 'range',
        min,
        max,
        direction: null,
        explicitCurrency: /\$/.test(term),
        label: `${formatBound(min)} – ${formatBound(max)}`,
      };
    }
  }

  const compare = term.match(COMPARE_RE);
  if (compare) {
    const value = toNumber(compare[2]);
    if (value !== null) {
      const operator = compare[1];
      if (operator === '=') {
        return exactQuery(value, compare[2], null, /\$/.test(term));
      }
      return {
        type: 'compare',
        operator,
        value,
        direction: null,
        explicitCurrency: /\$/.test(term),
        label: `${operator} ${formatBound(value)}`,
      };
    }
  }

  const signed = term.match(SIGNED_NUMBER_RE);
  if (signed) {
    const value = toNumber(signed[2]);
    if (value !== null) {
      const direction = signed[1] === '-' ? 'out' : signed[1] === '+' ? 'in' : null;
      return exactQuery(value, signed[2], direction, /\$/.test(term));
    }
  }

  return null;
}

/** Interprets a card last-digits search term, such as `*1234` or `card:1234`. */
export function parseCardQuery(rawTerm) {
  const term = String(rawTerm ?? '').trim();
  const match = term.match(CARD_QUERY_RE);
  return match ? { digits: match[1] } : null;
}

function exactQuery(value, token, direction, explicitCurrency) {
  const max = upperBound(value, token);
  return {
    type: 'exact',
    min: value,
    max,
    direction,
    explicitCurrency,
    label: max - value > EPSILON ? `${formatBound(value)} – ${formatBound(max)}` : formatBound(value),
  };
}

function formatBound(value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(value);
}

/** True when the transaction amount satisfies the parsed amount query. */
export function amountMatchesQuery(amount, query) {
  if (!query) return false;
  const raw = Number(amount);
  if (!Number.isFinite(raw)) return false;
  // Plaid signs outflows positive; the table renders them as -$x.
  if (query.direction === 'out' && !(raw > 0)) return false;
  if (query.direction === 'in' && !(raw < 0)) return false;

  const abs = Math.abs(raw);
  if (query.type === 'compare') {
    switch (query.operator) {
      case '>':
        return abs > query.value + EPSILON;
      case '>=':
        return abs >= query.value - EPSILON;
      case '<':
        return abs < query.value - EPSILON;
      case '<=':
        return abs <= query.value + EPSILON;
      default:
        return false;
    }
  }
  return abs >= query.min - EPSILON && abs <= query.max + EPSILON;
}

/**
 * Search that spans description, merchant, category and amount. Explicit card
 * syntax searches account last digits only. A bare number searches both text
 * and amounts; anything with `$`, an operator or a range is an amount-only
 * query so unrelated text hits don't dilute the result.
 */
export function transactionMatchesSearch(tx, rawTerm) {
  const term = String(rawTerm ?? '').trim();
  if (!term) return true;

  const cardQuery = parseCardQuery(term);
  if (cardQuery) return String(tx?.accountMask ?? '').endsWith(cardQuery.digits);

  const query = parseAmountQuery(term);
  if (query && amountMatchesQuery(tx?.amount, query)) return true;
  if (query && (query.explicitCurrency || query.type !== 'exact' || query.direction)) return false;

  const lowered = term.toLowerCase();
  return (
    String(tx?.name ?? '').toLowerCase().includes(lowered) ||
    String(tx?.merchantName ?? '').toLowerCase().includes(lowered) ||
    String(tx?.category ?? '').toLowerCase().includes(lowered)
  );
}
